import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import styles from './ActionPointModal.module.scss';
import {
  IconX,
  IconPlus
} from '@tabler/icons-react';

import { Status, ActionPointDto, TenantUserDto, ProjectDto } from '../../api/model';
import { usePostActionpoint } from '../../api/action-point-api/action-point-api';
import { getCheckpointTaskIdTask, getGetCheckpointTaskIdTaskQueryKey } from '../../api/check-point-api/check-point-api';
import { useGetProjectIdMembers } from '../../api/project-api/project-api';
import { useNotifications } from '../../hooks/useNotifications';
import { useProjectTasks } from '../../hooks/useProjectTasks';
import UserPicker from '../ui/UserPicker/UserPicker';
import { memberAsUser } from '../../utils/projectMembers';

interface CheckpointOption {
  id: number;
  taskName: string;
  validationCriteria?: string;
}

const cpLabel = (cp: CheckpointOption) =>
  `CP-${cp.id} – ${cp.validationCriteria || cp.taskName}`;

interface CheckpointComboboxProps {
  options: CheckpointOption[];
  value: number | null;
  onChange: (id: number | null) => void;
  search: string;
  onSearchChange: (s: string) => void;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  disabled?: boolean;
  placeholder?: string;
}

const CheckpointCombobox: React.FC<CheckpointComboboxProps> = ({
  options, value, onChange, search, onSearchChange, open, onOpenChange, disabled, placeholder,
}) => {
  const selected = options.find(o => o.id === value) ?? null;
  const filtered = options.filter(o =>
    cpLabel(o).toLowerCase().includes(search.toLowerCase())
  );

  const handleFocus = () => {
    onSearchChange('');
    onOpenChange(true);
  };

  const handleBlur = () => {
    setTimeout(() => onOpenChange(false), 160);
  };

  const displayValue = open ? search : (selected ? cpLabel(selected) : '');

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        className={styles.formInput}
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        onChange={e => { onSearchChange(e.target.value); onOpenChange(true); }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        autoComplete="off"
      />
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          zIndex: 200, background: '#fff',
          border: '1px solid #E5E7EB', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          maxHeight: 220, overflowY: 'auto',
        }}>
          {value !== null && (
            <div
              onMouseDown={(e) => { e.preventDefault(); onChange(null); onOpenChange(false); onSearchChange(''); }}
              style={{
                padding: '8px 12px', fontSize: 13, color: '#9CA3AF',
                cursor: 'pointer', borderBottom: '1px solid #F3F4F6',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#F9FAFB')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}
            >
              — Clear selection
            </div>
          )}
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 13, color: '#9CA3AF' }}>No checkpoints found</div>
          ) : filtered.map(cp => (
            <div
              key={cp.id}
              onMouseDown={(e) => { e.preventDefault(); onChange(cp.id); onOpenChange(false); onSearchChange(''); }}
              style={{
                padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                background: cp.id === value ? '#EFF6FF' : '',
                color: cp.id === value ? '#2563EB' : '#374151',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
              onMouseEnter={e => { if (cp.id !== value) e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseLeave={e => { if (cp.id !== value) e.currentTarget.style.background = ''; }}
            >
              <span style={{
                flexShrink: 0, padding: '2px 7px', borderRadius: 4,
                background: '#EFF6FF', color: '#2563EB',
                fontSize: 11, fontWeight: 700, border: '1px solid #BFDBFE',
              }}>
                CP-{cp.id}
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {cp.validationCriteria || cp.taskName}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface CreateActionPointModalProps {
  isOpen: boolean;
  onClose: () => void;
  onActionPointCreated?: () => void;
  project?: ProjectDto;
  projectId?: number;
  preselectedCheckpointId?: number;
}

interface ActionPointFormData {
  name: string;
  comment: string;
  validationCriteria: string;
  validationMethod: string;
  assignedUserIds: number[];
  status: Status;
  validationDate: string;
  actualValidationDate: string;
  checkPointId: number | '';
}

const CreateActionPointModal: React.FC<CreateActionPointModalProps> = ({
  isOpen,
  onClose,
  onActionPointCreated,
  project,
  projectId: projectIdProp,
  preselectedCheckpointId,
}) => {
  const projectId = project?.id ?? projectIdProp;
  const { t } = useTranslation('actionPoints');
  const [formData, setFormData] = useState<ActionPointFormData>({
    name: '',
    comment: '',
    validationCriteria: '',
    validationMethod: '',
    assignedUserIds: [],
    status: Status.PLANNED,
    validationDate: '',
    actualValidationDate: '',
    checkPointId: preselectedCheckpointId ?? '',
  });

  // Keep CPId in sync
  React.useEffect(() => {
    setFormData(prev => ({ ...prev, checkPointId: preselectedCheckpointId ?? '' }));
  }, [preselectedCheckpointId]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cpSearch, setCpSearch] = useState('');
  const [cpOpen, setCpOpen] = useState(false);

  // hooks
  const createActionPointMutation = usePostActionpoint();
  const { notifySuccess, notifyError } = useNotifications();
  const { data: projectMembers } = useGetProjectIdMembers(projectId ?? 0, {
    query: { enabled: !!projectId },
  });

  // Fetch all project tasks and checkpoints and populate at combobox
  const { tasks: projectTasks } = useProjectTasks(project);
  const checkpointQueries = useQueries({
    queries: projectTasks.map(task => ({
      queryKey: getGetCheckpointTaskIdTaskQueryKey(task.id),
      queryFn: ({ signal }: { signal?: AbortSignal }) => getCheckpointTaskIdTask(task.id!, signal),
      enabled: isOpen && !!task.id,
      staleTime: 2 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    })),
  });

  const availableCheckpoints = React.useMemo<CheckpointOption[]>(() => {
    const seen = new Set<number>();
    const result: CheckpointOption[] = [];
    checkpointQueries.forEach((q, i) => {
      const cp = q.data;
      if (cp?.id && !seen.has(cp.id)) {
        seen.add(cp.id);
        result.push({
          id: cp.id,
          taskName: projectTasks[i]?.name ?? `Task ${projectTasks[i]?.id}`,
          validationCriteria: cp.validationCriteria,
        });
      }
    });
    return result;
  }, [checkpointQueries, projectTasks]);

  const checkpointsLoading = isOpen && projectTasks.length > 0 && checkpointQueries.some(q => q.isFetching);
  const memberUsers = React.useMemo<TenantUserDto[]>(
    () => (projectMembers ?? []).map(memberAsUser),
    [projectMembers]
  );

  const handleInputChange = (field: keyof ActionPointFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) {
      return t('create.validation.nameRequired');
    }
    if (!formData.comment.trim()) {
      return t('create.validation.commentRequired');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      notifyError(t('create.title'), validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const actionPointData: ActionPointDto = {
        name: formData.name,
        comment: formData.comment,
        validationCriteria: formData.validationCriteria || undefined,
        validationMethod: formData.validationMethod || undefined,
        assignedUserIds: formData.assignedUserIds,
        status: formData.status,
        projectId: projectId,
        validationDate: formData.validationDate || undefined,
        actualValidationDate: formData.actualValidationDate || undefined,
        checkPointId: formData.checkPointId !== '' ? formData.checkPointId : undefined,
      };

      await createActionPointMutation.mutateAsync({
        data: actionPointData
      });

      notifySuccess(t('create.title'), t('create.validation.success', { name: formData.name }));
      onActionPointCreated?.();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error creating action point:', error);
      notifyError(t('create.title'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      comment: '',
      validationCriteria: '',
      validationMethod: '',
      assignedUserIds: [],
      status: Status.PLANNED,
      validationDate: '',
      actualValidationDate: '',
      checkPointId: preselectedCheckpointId ?? '',
    });
  };

  const handleClose = () => {
    resetForm();
    setCpSearch('');
    setCpOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={handleClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <div className={styles.headerContent}>
              <h2 className={styles.modalTitle}>{t('create.title')}</h2>
              <p className={styles.headerDescription}>{t('create.description')}</p>
            </div>
            <button className={styles.closeButton} onClick={handleClose} type="button">
              <IconX size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={styles.modalBody}>
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('create.sections.basic')}</h3>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.name').replace(' *','')}
                    <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.formInput}
                    placeholder={t('create.fields.namePlaceholder')}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.comment').replace(' *','')}
                    <span className={styles.required}>*</span>
                  </label>
                  <textarea
                    className={styles.formTextarea}
                    placeholder={t('create.fields.commentPlaceholder')}
                    value={formData.comment}
                    onChange={(e) => handleInputChange('comment', e.target.value)}
                    disabled={isSubmitting}
                    rows={4}
                    required
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('create.sections.validation')}</h3>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.validationCriteria')}
                  </label>
                  <input
                    type="text"
                    className={styles.formInput}
                    placeholder={t('create.fields.validationCriteriaPlaceholder')}
                    value={formData.validationCriteria}
                    onChange={(e) => handleInputChange('validationCriteria', e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.validationMethod')}
                  </label>
                  <input
                    type="text"
                    className={styles.formInput}
                    placeholder={t('create.fields.validationMethodPlaceholder')}
                    value={formData.validationMethod}
                    onChange={(e) => handleInputChange('validationMethod', e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('create.fields.validationDate', 'Validation Date')}</label>
                    <input
                      type="date"
                      className={styles.formInput}
                      value={formData.validationDate}
                      onChange={(e) => handleInputChange('validationDate', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('create.fields.actualValidationDate', 'Actual Validation Date')}</label>
                    <input
                      type="date"
                      className={styles.formInput}
                      value={formData.actualValidationDate}
                      onChange={(e) => handleInputChange('actualValidationDate', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              {/* Checkpoint link */}
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('create.sections.checkpoint', 'Checkpoint Link')}</h3>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.checkpointId', 'Parent Checkpoint')}
                    {checkpointsLoading && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>
                        {t('create.fields.checkpointLoading', 'Loading…')}
                      </span>
                    )}
                  </label>
                  <CheckpointCombobox
                    options={availableCheckpoints}
                    value={formData.checkPointId !== '' ? formData.checkPointId : null}
                    onChange={(id) => handleInputChange('checkPointId', id ?? '')}
                    search={cpSearch}
                    onSearchChange={setCpSearch}
                    open={cpOpen}
                    onOpenChange={setCpOpen}
                    disabled={isSubmitting}
                    placeholder={
                      checkpointsLoading
                        ? t('create.fields.checkpointLoadingPlaceholder', 'Loading checkpoints…')
                        : availableCheckpoints.length === 0
                          ? t('create.fields.checkpointNoData', 'No checkpoints available')
                          : t('create.fields.checkpointIdPlaceholder', 'Search checkpoint…')
                    }
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('create.sections.assignment')}</h3>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.assignedPerson')}
                  </label>
                  <UserPicker
                    multi
                    value={formData.assignedUserIds}
                    onChange={(ids) => handleInputChange('assignedUserIds', ids)}
                    disabled={isSubmitting}
                    placeholder={t('create.fields.assignedPersonPlaceholder')}
                    users={projectId ? memberUsers : undefined}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.status')}
                  </label>
                  <select
                    className={styles.formSelect}
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value as Status)}
                    disabled={isSubmitting}
                  >
                    <option value={Status.PLANNED}>{t('status.planned')}</option>
                    <option value={Status.IN_PROGRESS}>{t('status.inProgress')}</option>
                    <option value={Status.DELAYED}>{t('status.delayed')}</option>
                    <option value={Status.FINISHED}>{t('status.finished')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <div className={styles.footerActions} style={{ marginLeft: 'auto' }}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {t('create.buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  <IconPlus size={18} />
                  {isSubmitting ? t('create.buttons.creating') : t('create.buttons.create')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateActionPointModal;
